// Element picker: document-level capture listeners while armed. The highlight
// layer is pointer-events:none, so hover targets come straight from events.
// While armed, page interactions are intercepted (capture phase) so picking a
// button reports it instead of pressing it. A single cursor-only <style> tag is
// the one thing added to the host document; removed on disarm (zero residue).

export interface PickerCallbacks {
  isArmed(): boolean
  isOurs(e: Event): boolean
  onHover(el: Element): void
  onPick(el: Element): void
}

const CURSOR_CSS = 'html, html * { cursor: crosshair !important; }'

export class Picker {
  private cbs: PickerCallbacks
  private cursorStyle: HTMLStyleElement | null = null
  private focusables: Element[] = []
  private focusIdx = -1
  private listeners: Array<[string, EventListener]> = []

  constructor(cbs: PickerCallbacks) {
    this.cbs = cbs
  }

  attach(): void {
    const on = (type: string, handler: EventListener) => {
      document.addEventListener(type, handler, true)
      this.listeners.push([type, handler])
    }
    on('mousemove', (e) => {
      if (!this.cbs.isArmed() || this.cbs.isOurs(e)) return
      const el = this.pageElement(e.target)
      if (el) this.cbs.onHover(el)
    })
    for (const type of ['pointerdown', 'mousedown', 'mouseup']) {
      on(type, (e) => {
        if (!this.cbs.isArmed() || this.cbs.isOurs(e)) return
        e.preventDefault()
        e.stopPropagation()
      })
    }
    on('click', (e) => {
      if (!this.cbs.isArmed() || this.cbs.isOurs(e)) return
      e.preventDefault()
      e.stopPropagation()
      const el = this.pageElement(e.target)
      if (el) this.cbs.onPick(el)
    })
    // Keyboard-only picking: Tab cycles focusable host elements, Enter locks.
    on('keydown', (e) => {
      const ke = e as KeyboardEvent
      if (!this.cbs.isArmed() || this.cbs.isOurs(e)) return
      if (ke.key === 'Tab') {
        ke.preventDefault()
        ke.stopPropagation()
        this.cycleFocus(ke.shiftKey ? -1 : 1)
      } else if (ke.key === 'Enter' && this.focusIdx >= 0) {
        ke.preventDefault()
        ke.stopPropagation()
        this.cbs.onPick(this.focusables[this.focusIdx])
      }
    })
  }

  detach(): void {
    for (const [type, handler] of this.listeners) document.removeEventListener(type, handler, true)
    this.listeners = []
    this.setCursor(false)
  }

  setCursor(active: boolean): void {
    if (active && !this.cursorStyle) {
      this.cursorStyle = document.createElement('style')
      this.cursorStyle.setAttribute('data-qc-lens', 'cursor')
      this.cursorStyle.textContent = CURSOR_CSS
      document.head.appendChild(this.cursorStyle)
    } else if (!active && this.cursorStyle) {
      this.cursorStyle.remove()
      this.cursorStyle = null
      this.focusables = []
      this.focusIdx = -1
    }
  }

  private cycleFocus(dir: 1 | -1): void {
    if (!this.focusables.length) {
      this.focusables = Array.from(
        document.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => el.tagName.toLowerCase() !== 'qc-lens' && (el as HTMLElement).offsetParent !== null)
      if (!this.focusables.length) return
    }
    this.focusIdx = (this.focusIdx + dir + this.focusables.length) % this.focusables.length
    const el = this.focusables[this.focusIdx]
    el.scrollIntoView({ block: 'nearest' })
    this.cbs.onHover(el)
  }

  private pageElement(target: EventTarget | null): Element | null {
    if (!(target instanceof Element)) return null
    const tag = target.tagName.toLowerCase()
    if (tag === 'html' || tag === 'body' || tag === 'qc-lens') return null
    return target
  }
}
